import { cloneDeep } from 'lodash-es';
import { nextTick, Ref, ShallowRef, ComputedRef } from 'vue';
import type { ColumnSizingState } from '@tanstack/vue-table';
import type {
  GridColumnDef,
  GridRawRowDataType,
  ColumnVisibilityInfo,
  GridRowSelectionOption,
  GridDetailRowOption,
} from '../../modernGrid.types';
import {
  EXPAND_BTN_COLUMN_ID,
  CHECKBOX_COLUMN_ID,
  NUMBER_COLUMN_ID,
  DEFAULT_EXTRA_COLUMN_SIZE,
  DEFAULT_COLUMN_MIN_SIZE,
} from '../../modernGrid.define';
import { existObjectValue } from '../../modernGrid.utils';

interface UseColumnSettingParams<RowDataType extends GridRawRowDataType> {
  props: {
    columns: GridColumnDef<RowDataType>[];
    columnSize?: ColumnSizingState;
  };
  columnSettingList: Ref<ColumnVisibilityInfo[]>;
  columnVisibilityState: Ref<Record<string, boolean>>;
  processedColumns: ShallowRef<GridColumnDef<RowDataType>[] | undefined>;
  hasGroupedColumns: Ref<boolean>;
  envKey: Ref<string | undefined>;
  useNumberColumn: ComputedRef<boolean>;
  detailRowInfo: ComputedRef<GridDetailRowOption>;
  rowSelectionInfo: ComputedRef<GridRowSelectionOption>;
  getEnv: (key: string | undefined) => any;
  setColumnVisibilityState: () => void;
  setExpandingState: () => void;
  setColumnOrderAndPinningState: () => void;
  setColumnSizingState: (columnSize?: ColumnSizingState) => void;
  setGroupingState: () => void;
  setSortingState: () => void;
  setRowSelectionState: () => void;
  setColumnVisibility: (updatedColumnList: ColumnVisibilityInfo[]) => Promise<void>;
}

/**
 * Column 초기화 및 설정 관련 로직을 제공하는 composable
 * 컬럼 설정 초기화, 리셋, 사이징 업데이트, 그리드 상태 동기화 등의 기능을 제공
 */
export const useColumnSetting = <RowDataType extends GridRawRowDataType>(
  params: UseColumnSettingParams<RowDataType>,
) => {
  const {
    props,
    columnSettingList,
    columnVisibilityState,
    processedColumns,
    hasGroupedColumns,
    envKey,
    useNumberColumn,
    detailRowInfo,
    rowSelectionInfo,
    getEnv,
    setColumnVisibilityState,
    setExpandingState,
    setColumnOrderAndPinningState,
    setColumnSizingState,
    setGroupingState,
    setSortingState,
    setRowSelectionState,
    setColumnVisibility,
  } = params;

  // 그리드의 모든 상태를 동기화
  const syncGridState = () => {
    setColumnVisibilityState();
    setExpandingState();
    setColumnOrderAndPinningState();
    setColumnSizingState(props.columnSize);
    setGroupingState();
    setSortingState();
    setRowSelectionState();
  };

  // 컬럼 설정을 초기화
  // 재귀적으로 그룹 컬럼을 처리하고, 기본 컬럼(번호, 체크박스, 확장 버튼)을 추가
  const initColumnSetting = (
    columnList: GridColumnDef<RowDataType>[],
    parentHeader?: string,
    isFirst = true,
  ) => {
    if (isFirst) {
      columnSettingList.value = [];
    }

    const envColumnVisibility = getEnv(envKey.value)?.columnVisibility;
    const existEnv = existObjectValue(envColumnVisibility);
    const newColumnList = [...columnList];
    newColumnList?.forEach((column) => {
      const { id, accessorKey, header, enableHiding, meta, columns: subColumns } = column;
      const label = parentHeader ? `${parentHeader} ${header as string}` : (header as string);
      const columnId = id || accessorKey;
      if (!columnId) return;

      if (subColumns) {
        hasGroupedColumns.value = true;
        initColumnSetting(subColumns as GridColumnDef<RowDataType>[], header as string, false);
        return;
      }

      const visibilitySetting = !(meta?.hiddenDisplay === true);

      if (!meta?.excludeFromColumnList) {
        columnSettingList.value.push({
          text: columnId,
          fixed: enableHiding === false,
          label,
          visibility: visibilitySetting,
        });
      }

      if (!existEnv) {
        columnVisibilityState.value[columnId] = visibilitySetting;
      }
      if (column.meta && !Number.isFinite(column.meta.size)) {
        column.size = undefined;
        column.meta.size = undefined;
      }
      if (column.meta?.size && column.meta.size < DEFAULT_COLUMN_MIN_SIZE) {
        column.minSize = column.meta.size;
      }
    });

    if (detailRowInfo.value?.use) {
      newColumnList.unshift({
        id: EXPAND_BTN_COLUMN_ID,
        header: '',
        size: DEFAULT_EXTRA_COLUMN_SIZE,
        minSize: DEFAULT_EXTRA_COLUMN_SIZE,
        enableGlobalFilter: false,
        enableSorting: false,
        enableResizing: false,
        enableHiding: false,
        meta: {
          align: 'center',
          size: DEFAULT_EXTRA_COLUMN_SIZE,
        },
      });
    }
    if (rowSelectionInfo.value?.useCheckbox) {
      newColumnList.unshift({
        id: CHECKBOX_COLUMN_ID,
        header: '',
        size: DEFAULT_EXTRA_COLUMN_SIZE,
        minSize: DEFAULT_EXTRA_COLUMN_SIZE,
        enableGlobalFilter: false,
        enableSorting: false,
        enableResizing: false,
        enableHiding: false,
        meta: {
          align: 'center',
          size: DEFAULT_EXTRA_COLUMN_SIZE,
        },
      });
    }
    if (useNumberColumn.value) {
      newColumnList.unshift({
        id: NUMBER_COLUMN_ID,
        header: 'No.',
        size: DEFAULT_EXTRA_COLUMN_SIZE,
        minSize: DEFAULT_EXTRA_COLUMN_SIZE,
        enableGlobalFilter: false,
        enableSorting: true,
        enableResizing: true,
        enableHiding: false,
        meta: {
          align: 'center',
          size: DEFAULT_EXTRA_COLUMN_SIZE,
        },
      });
    }

    processedColumns.value = cloneDeep(newColumnList);
  };

  // 컬럼 설정을 초기 상태로 리셋
  const resetColumnSettings = async () => {
    columnVisibilityState.value = {};
    columnSettingList.value = [];
    processedColumns.value = [];

    // 마지막 컬럼 드래그 후 다시 마지막 위치로 이동했을 때 DOM 의 위치가 정상적으로 업데이트 되지 않아
    // DOM 초기화를 위해 Vue가 리렌더링을 트리거하도록 대기
    await nextTick();

    initColumnSetting(props.columns || []);
  };

  // 컬럼 사이징 상태를 업데이트
  const updateColumnSizingState = async (
    updatedColumnList: ColumnVisibilityInfo[],
  ): Promise<void> => {
    await setColumnVisibility(updatedColumnList);
    setColumnSizingState();
  };

  return {
    syncGridState,
    initColumnSetting,
    resetColumnSettings,
    updateColumnSizingState,
  };
};
